/**
 * GSAP, scoped to a component.
 *
 * Everything the setup function creates lives in one gsap.context, so on
 * unmount it is all reverted: tweens killed, ScrollTriggers removed, inline
 * styles cleared. Selector strings inside setup only match within `scope`.
 *
 * Under reduced motion setup never runs and the page keeps its final,
 * static state — so write the CSS for the end state and animate *from* it.
 *
 * Usage:
 *   const root = ref(null)
 *   useGsap(root, (gsap) => {
 *     gsap.from(".card", { y: 24, opacity: 0, scrollTrigger: { trigger: ".card" } })
 *   })
 */
import { onMounted, onBeforeUnmount, unref } from "vue";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { prefersReducedMotion } from "./useScrollReveal";

gsap.registerPlugin(ScrollTrigger);

/*
 * ScrollTrigger measures trigger positions once, and again on the window's
 * load and resize. Lazy images and fonts keep growing the page after that,
 * which left triggers firing hundreds of pixels early. Re-measure whenever
 * the page's height settles on a new value.
 */
let watchingHeight = false;

function refreshOnHeightChange() {
  if (watchingHeight || !("ResizeObserver" in window)) return;
  watchingHeight = true;

  let timer = null;
  let height = document.body.offsetHeight;
  new ResizeObserver(() => {
    if (document.body.offsetHeight === height) return;
    height = document.body.offsetHeight;
    clearTimeout(timer);
    timer = setTimeout(() => ScrollTrigger.refresh(), 150);
  }).observe(document.body);
}

export function useGsap(scope, setup) {
  let ctx = null;

  onMounted(() => {
    const el = unref(scope);
    if (!el || prefersReducedMotion()) return;
    refreshOnHeightChange();
    ctx = gsap.context(() => setup(gsap, el), el);
  });

  onBeforeUnmount(() => {
    ctx?.revert();
    ctx = null;
  });
}

export { gsap, ScrollTrigger };
